import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { supabase, handleAuthError } from "../lib/supabaseClient";
import { useNavigate, useLocation } from "react-router-dom";

const UserContext = createContext();

// 전역 캐시
const userCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5분
const TAB_SWITCH_IGNORE_DURATION = 2000; // 탭 전환 후 무시할 시간 (2초)
const SESSION_EVENT_THROTTLE = 1000; // 세션 이벤트 스로틀 시간 (1초)
const FETCH_DEBOUNCE_DELAY = 500; // fetch 디바운스 지연 시간 (500ms)

// 빈 userInfo 객체 상수
const EMPTY_USER_INFO = {
  email: "",
  name: "",
  class: "",
  role: "",
  company: "",
  grade: "",
};

// 공개 경로 목록
const PUBLIC_PATHS = [
  "/", // 공개 소개 랜딩 — 비로그인 접근 허용 (로그인 상태 분기는 Landing 이 처리)
  "/signup",
  "/login",
  "/mac-mini", // Apple 스타일 디자인 데모 — 비로그인 접근 허용
  "/demo", // 외부 소개용 데모 사이트(샘플 데이터, public/demo/index.html) — 비로그인 접근 허용
  "/terms-of-service",
  "/privacy-policy",
  "/reset-password",
  "/watcha-shorts",
  "/modify-video",
  "/withdrawal",
  "/partner-check", // 파트너 확인 페이지(공개) — 질의문자(?c=) 방식이라 정확 일치로 충분 (2026-09-22)
];

export function UserProvider({ children }) {
  const [userInfo, setUserInfo] = useState(EMPTY_USER_INFO);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const lastFetchedEmail = useRef("");
  const fetchTimeoutRef = useRef(null);
  const tabSwitchTimeoutRef = useRef(null);
  const isFetching = useRef(false);
  const lastSessionEvent = useRef(null);
  const isInitialMount = useRef(true);
  const tabVisibilityChangeTime = useRef(null);
  const isTabSwitching = useRef(false);

  // 탭 전환 중인지 확인하는 헬퍼 함수
  const isTabSwitchingNow = useCallback(() => {
    if (!isTabSwitching.current || !tabVisibilityChangeTime.current) return false;
    return Date.now() - tabVisibilityChangeTime.current < TAB_SWITCH_IGNORE_DURATION;
  }, []);

  // 탭 전환 상태 설정 헬퍼 함수
  const setTabSwitching = useCallback((value) => {
    if (!value) return;
    
    isTabSwitching.current = true;
    tabVisibilityChangeTime.current = Date.now();
    
    // 기존 타임아웃 클리어
    if (tabSwitchTimeoutRef.current) {
      clearTimeout(tabSwitchTimeoutRef.current);
    }
    
    // 일정 시간 후 자동 해제
    tabSwitchTimeoutRef.current = setTimeout(() => {
      isTabSwitching.current = false;
    }, TAB_SWITCH_IGNORE_DURATION);
  }, []);

  const fetchUserInfoFromSession = useCallback(async (session) => {
    // 세션이 없으면 현재 세션을 가져와서 시도
    if (!session) {
      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        if (error) {
          // 세션 가져오기 실패 시 인증 에러인지 확인하고 토큰 삭제
          await handleAuthError(error, 'main');
        }
        session = currentSession;
      } catch (error) {
        // 예상치 못한 에러 발생 시에도 처리
        await handleAuthError(error, 'main');
        session = null;
      }
    }
    // iframe 안 (어드민 사용자 정산 미러 페이지) 에서는 별도 키 (`adminViewImpersonateEmail`) 를 사용하여
    // 부모 admin 컨텍스트가 throttled refresh 도중 impersonate 로 흔들리는 것을 방지한다.
    const isInIframe = typeof window !== "undefined" && window.self !== window.top;
    const impersonateEmailKey = isInIframe ? "adminViewImpersonateEmail" : "impersonateEmail";
    const isActualAdminKey = isInIframe ? "adminViewIsActualAdmin" : "isActualAdmin";

    const impersonateEmail = localStorage.getItem(impersonateEmailKey);
    let targetEmail = session?.user?.email;

    if (impersonateEmail) {
      const actualEmail = session?.user?.email;
      if (actualEmail) {
        const { data: actualUser } = await supabase
          .from("user")
          .select("role")
          .eq("email", actualEmail)
          .single();

        if (actualUser?.role?.includes("Admin")) {
          targetEmail = impersonateEmail;
          localStorage.setItem(isActualAdminKey, "true");
        } else {
          localStorage.removeItem(impersonateEmailKey);
          localStorage.removeItem(isActualAdminKey);
        }
      }
    }
    
    // /home 경로로 접속할 때는 캐시를 무시하고 무조건 새로운 데이터를 가져옴
    const isHomeRoute = location.pathname === "/home";
    
    // 이메일이 변경되지 않았고 홈 경로가 아니면 호출하지 않음
    if (targetEmail === lastFetchedEmail.current && !isHomeRoute) {
      return;
    }
    
    // 이미 fetch 중이면 중단
    if (isFetching.current) {
      return;
    }
    
    // 세션 이벤트가 너무 자주 발생하면 무시 (스로틀링)
    const now = Date.now();
    if (lastSessionEvent.current && (now - lastSessionEvent.current) < SESSION_EVENT_THROTTLE) {
      return;
    }
    lastSessionEvent.current = now;
    
    // 이전 타임아웃 클리어
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    // 로그인 직후이거나 홈 경로인 경우 즉시 실행, 그 외에는 디바운싱
    const shouldExecuteImmediately = isHomeRoute || (session?.user && !lastFetchedEmail.current);
    
    const executeFetch = async () => {
      // 홈 경로가 아니면 캐시 확인
      if (!isHomeRoute && targetEmail && userCache.has(targetEmail)) {
        const cached = userCache.get(targetEmail);
        if (Date.now() - cached.timestamp < CACHE_DURATION) {
          setUserInfo(cached.data);
          // grade가 "CUT"이면 /total 페이지만 접근 허용
          if (cached.data.grade === "CUT" && location.pathname !== "/total") {
            navigate("/total", { replace: true });
          }
          lastFetchedEmail.current = targetEmail;
          setIsLoading(false);
          return;
        }
      }
      
      isFetching.current = true;
      setIsLoading(true);
      lastFetchedEmail.current = targetEmail;
      
      try {
        if (targetEmail) {
          const { data, error } = await supabase
            .from("user")
            .select("email, name, class, role, company, is_not_contract, grade")
            .eq("email", targetEmail)
            .maybeSingle();
          
          // 인증 에러인 경우 토큰 삭제
          if (error) {
            await handleAuthError(error, 'main');
          }
          
          if (data) {
            setUserInfo(data);
            
            // grade가 "CUT"이면 /total 페이지만 접근 허용
            if (data.grade === "CUT" && location.pathname !== "/total") {
              navigate("/total", { replace: true });
            }
            
            // 홈 경로가 아니면 캐시에 저장
            if (!isHomeRoute) {
              userCache.set(targetEmail, {
                data,
                timestamp: Date.now()
              });
            }
          } else {
            setUserInfo(EMPTY_USER_INFO);
          }
        } else {
          setUserInfo(EMPTY_USER_INFO);
          // 공개 경로가 아니면 로그인 페이지로 리다이렉트
          if (!PUBLIC_PATHS.includes(location.pathname)) {
            navigate("/login", { replace: true });
          }
        }
      } catch (error) {
        console.error("User fetch error:", error);
        // 예상치 못한 에러 발생 시에도 인증 에러인지 확인하고 토큰 삭제
        await handleAuthError(error, 'main');
      } finally {
        setIsLoading(false);
        isFetching.current = false;
      }
    };
    
    if (shouldExecuteImmediately) {
      executeFetch();
    } else {
      // 디바운싱: 일정 시간 후에 실행
      fetchTimeoutRef.current = setTimeout(executeFetch, FETCH_DEBOUNCE_DELAY);
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    // 탭 전환 감지 (visibilitychange 이벤트)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setTabSwitching(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 세션 변경 이벤트를 한 번만 등록
    const result = supabase.auth.onAuthStateChange((event, session) => {
      // TOKEN_REFRESHED 이벤트는 탭 전환 시 자동으로 발생하므로 무시
      if (event === 'TOKEN_REFRESHED') {
        return;
      }
      
      // 탭 전환 중이면 모든 이벤트 무시
      if (isTabSwitchingNow()) {
        return;
      }
      
      // 초기 마운트가 아닌 경우에만 실행
      if (!isInitialMount.current) {
        fetchUserInfoFromSession(session);
      }
    });
    
    // 커스텀 이벤트 리스너 추가 (로그인 성공 후 즉시 실행을 위해)
    const handleCustomAuthEvent = (event) => {
      // 탭 전환 중이면 무시
      if (isTabSwitchingNow()) {
        return;
      }
      
      if (event.detail?.session) {
        fetchUserInfoFromSession(event.detail.session);
      }
    };
    
    window.addEventListener('authStateChange', handleCustomAuthEvent);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      if (tabSwitchTimeoutRef.current) {
        clearTimeout(tabSwitchTimeoutRef.current);
      }
      isFetching.current = false;
      window.removeEventListener('authStateChange', handleCustomAuthEvent);
      if (typeof result === "function") {
        result();
      } else if (result?.data?.unsubscribe) {
        result.data.unsubscribe();
      } else if (result?.unsubscribe) {
        result.unsubscribe();
      }
    };
  }, [navigate, fetchUserInfoFromSession, isTabSwitchingNow, setTabSwitching]);

  // 초기 로딩 시 세션 확인 (마운트 시 한 번만 실행)
  useEffect(() => {
    const checkInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          await handleAuthError(error, 'main');
          setIsLoading(false);
          isInitialMount.current = false;
          return;
        }
        
        if (session) {
          fetchUserInfoFromSession(session);
        } else {
          setIsLoading(false);
        }
        
        isInitialMount.current = false;
      } catch (error) {
        await handleAuthError(error, 'main');
        setIsLoading(false);
        isInitialMount.current = false;
      }
    };
    
    checkInitialSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 마운트 시 한 번만 실행

  // 경로 변경 시 grade 체크
  useEffect(() => {
    if (userInfo.grade === "CUT" && location.pathname !== "/total") {
      navigate("/total", { replace: true });
    }
  }, [location.pathname, userInfo.grade, navigate]);

  // Context value 메모이제이션으로 불필요한 재렌더링 방지
  const contextValue = useMemo(
    () => ({ userInfo, setUserInfo, isLoading }),
    [userInfo, isLoading]
  );

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
